import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, ShieldCheck, AlertTriangle, Building2 } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { RoleFormDrawer } from "./RoleFormDrawer";
import { roleApi, bizRoleApi } from "../../../lib/roleApi";
import { useAuth } from "../../../hooks/useAuth";
import { PAGE_LABELS } from "../../../types/pages";
import type { PageKey } from "../../../types/pages";
import type { Role } from "../../../types/index";

export default function ManageRolesPage() {
  const queryClient = useQueryClient();
  const { toasts, toast, dismissToast } = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | undefined>(undefined);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmRole, setConfirmRole] = useState<Role | null>(null);

  const { activeBusinessId, businesses } = useAuth();
  const isBizScope = !!activeBusinessId;
  const activeBusinessName = businesses.find((b) => b.businessId === activeBusinessId)?.name;
  const queryKey = isBizScope ? ["biz-roles", activeBusinessId] : ["admin-roles"];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      isBizScope ? bizRoleApi.list(activeBusinessId!) : roleApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof roleApi.create>[0]) =>
      isBizScope ? bizRoleApi.create(activeBusinessId!, payload) : roleApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Role created successfully");
      setDrawerOpen(false);
    },
    onError: () => toast.error("Failed to create role"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ roleId, payload }: { roleId: string; payload: Parameters<typeof roleApi.update>[1] }) =>
      isBizScope
        ? bizRoleApi.update(activeBusinessId!, roleId, payload)
        : roleApi.update(roleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Role updated successfully");
      setDrawerOpen(false);
      setEditingRole(undefined);
    },
    onError: () => toast.error("Failed to update role"),
  });

  const deleteMutation = useMutation({
    mutationFn: (roleId: string) =>
      isBizScope ? bizRoleApi.delete(activeBusinessId!, roleId) : roleApi.delete(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Role deleted");
      setDeletingId(null);
      setConfirmRole(null);
    },
    onError: () => {
      toast.error("Cannot delete this role");
      setDeletingId(null);
      setConfirmRole(null);
    },
  });

  const handleCreate = () => {
    setEditingRole(undefined);
    setDrawerOpen(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setDrawerOpen(true);
  };

  const handleDelete = (role: Role) => {
    if (role.isSystem) {
      toast.error("System roles cannot be deleted");
      return;
    }
    setConfirmRole(role);
  };

  const handleConfirmDelete = () => {
    if (!confirmRole) return;
    setDeletingId(confirmRole.roleId);
    deleteMutation.mutate(confirmRole.roleId);
  };

  const handleDrawerSubmit = async (values: { name: string; description?: string; pages: PageKey[] }) => {
    if (editingRole) {
      await updateMutation.mutateAsync({ roleId: editingRole.roleId, payload: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  const roles = data?.items ?? [];

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#0f172a]">Manage Roles</h1>
          {isBizScope ? (
            <div className="mt-1 flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5 text-[#2447d9]" />
              <span className="text-sm text-[#64748b]">
                Roles for <span className="font-medium text-[#2447d9]">{activeBusinessName ?? activeBusinessId}</span>
              </span>
            </div>
          ) : (
            <p className="mt-0.5 text-sm text-[#64748b]">
              Create and configure roles that control which pages employees can access.
            </p>
          )}
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          New Role
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-[#fca5a5] bg-[#fef2f2] p-4 text-sm text-[#dc2626]">
          Failed to load roles. Please try again.
        </div>
      ) : roles.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e2e8f0] bg-white py-16 text-center">
          <ShieldCheck className="h-10 w-10 text-[#cbd5e1] mb-3" />
          <p className="text-sm font-medium text-[#64748b]">No custom roles yet</p>
          <p className="text-xs text-[#94a3b8] mt-1">Create a role to control page access for employees.</p>
          <Button onClick={handleCreate} className="mt-4 gap-2">
            <Plus className="h-4 w-4" />
            Create First Role
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {roles.map((role) => (
            <RoleCard
              key={role.roleId}
              role={role}
              onEdit={handleEdit}
              onDelete={handleDelete}
              deleting={deletingId === role.roleId}
            />
          ))}
        </div>
      )}

      {/* Drawer */}
      {drawerOpen && (
        <RoleFormDrawer
          role={editingRole}
          onClose={() => {
            setDrawerOpen(false);
            setEditingRole(undefined);
          }}
          onSubmit={handleDrawerSubmit}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fef2f2]">
                <AlertTriangle className="h-5 w-5 text-[#dc2626]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#0f172a]">Delete role</h2>
                <p className="mt-1 text-sm text-[#64748b]">
                  Are you sure you want to delete <span className="font-medium text-[#0f172a]">{confirmRole.name}</span>?
                  This cannot be undone and employees assigned this role will lose their page access.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirmRole(null)}
                disabled={deleteMutation.isPending}
                className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#475569] hover:bg-[#f8fafc] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-[#dc2626] px-4 py-2 text-sm font-medium text-white hover:bg-[#b91c1c] disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleCard({
  role,
  onEdit,
  onDelete,
  deleting,
}: {
  role: Role;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
  deleting: boolean;
}) {
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="h-5 w-5 text-[#2447d9] shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-[#0f172a] truncate">{role.name}</p>
            {role.isSystem && (
              <span className="inline-block text-[10px] font-medium uppercase tracking-wide text-[#2447d9] bg-[#eff2ff] rounded px-1.5 py-0.5 mt-0.5">
                System
              </span>
            )}
          </div>
        </div>
        {!role.isSystem && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onEdit(role)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#2447d9] transition-colors"
              aria-label={`Edit ${role.name}`}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(role)}
              disabled={deleting}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#64748b] hover:bg-[#fef2f2] hover:text-[#dc2626] transition-colors disabled:opacity-40"
              aria-label={`Delete ${role.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {role.description && (
        <p className="mt-2 text-xs text-[#64748b] line-clamp-2">{role.description}</p>
      )}

      <div className="mt-3 pt-3 border-t border-[#f1f5f9]">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8] mb-2">
          Pages ({role.pages.length})
        </p>
        <div className="flex flex-wrap gap-1.5">
          {role.pages.map((page) => (
            <span
              key={page}
              className="inline-block rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[11px] text-[#475569]"
            >
              {PAGE_LABELS[page as PageKey] ?? page}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
